import { Box, Stack, Text } from "@mantine/core"
import { StepperRail } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { SUPPLIER_ONBOARDING_STEP_IDS } from "~/pages/SupplierPortal/Onboarding/constants/supplierOnboardingSteps"
import { useOnboardingContext } from "~/pages/SupplierPortal/Onboarding/OnboardingContext"
import { StepperFooterNav } from "~/pages/SupplierPortal/Onboarding/StepperFooterNav"
import { StepperStepHeader } from "~/pages/SupplierPortal/Onboarding/StepperStepHeader"
import { WelcomeStep } from "~/pages/SupplierPortal/Onboarding/welcomeStep/WelcomeStep"
import { VerifyCompanyOutcomeStep } from "~/pages/SupplierPortal/Onboarding/verifyCompanyOutcome/VerifyCompanyOutcomeStep"

export const OnboardingStepperShell = () => {
  const { t } = useTranslation("supplier")
  const {
    steps,
    currentStep,
    currentStepIndex,
    currentStepId,
    isFirstStep,
    isLastStep,
    goToPreviousStep,
    goToNextStep,
    verifyCompanyOutcome,
  } = useOnboardingContext()

  const handleNext = (): void => {
    if (currentStepId === SUPPLIER_ONBOARDING_STEP_IDS.verify_company) {
      verifyCompanyOutcome.handleContinue()
      return
    }

    goToNextStep()
  }

  const stepBody =
    currentStepId === SUPPLIER_ONBOARDING_STEP_IDS.welcome ? (
      <WelcomeStep />
    ) : currentStepId === SUPPLIER_ONBOARDING_STEP_IDS.verify_company ? (
      <VerifyCompanyOutcomeStep {...verifyCompanyOutcome} />
    ) : (
      <Text component="p" size="sm" fw={400} c="dimmed" fs="italic" m={0}>
        {t("onboarding.placeholder")}
      </Text>
    )

  return (
    <Stack
      maw={720}
      mx="auto"
      w="100%"
      flex={1}
      gap="var(--mantine-spacing-lg)"
      style={{ minHeight: 0 }}
    >
      <StepperRail
        ariaLabel={t("onboarding.ariaLabel")}
        currentStepIndex={currentStepIndex}
        steps={steps.map((step) => ({
          id: step.id,
          label: step.name,
          stepNumber: step.stepNumber,
        }))}
      />
      <Stack
        component="section"
        flex={1}
        gap="var(--tfg-organism-padding-y)"
        px="var(--tfg-organism-padding-x)"
        py="var(--tfg-organism-padding-y)"
        bg="var(--mantine-color-white)"
        bd="1px solid var(--mantine-color-ground-1)"
        style={{
          borderRadius: "var(--tfg-card-border-radius)",
          minHeight: 0,
          maxHeight: "min(720px, calc(100dvh - 200px))",
        }}
      >
        <StepperStepHeader
          eyebrow={t("onboarding.stepIndicator", {
            current: currentStep.stepNumber,
            total: steps.length,
          })}
          title={currentStep.title}
          subtitle={currentStep.subtitle}
        />
        <Box
          py="var(--mantine-spacing-sm)"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {stepBody}
        </Box>
        <StepperFooterNav
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          onBack={goToPreviousStep}
          onNext={handleNext}
        />
      </Stack>
    </Stack>
  )
}
